/**
 * Session context — the result of GET /me, available app-wide.
 *
 * SessionProvider sits inside AuthGuard (so it only runs when the user
 * is authenticated) and MsalProvider (so acquireTokenSilent works).
 * It fetches /me exactly once per page load, caches the result in React
 * Query, and exposes it via the `useSession` hook.
 *
 * Design choices:
 *   - React Query handles caching, retry, and stale-while-revalidate. We
 *     set `staleTime: Infinity` because /me is essentially immutable for the
 *     lifetime of the browser tab — role changes require a sign-out/in.
 *   - The context value is never undefined inside SessionProvider; callers
 *     that useSession() outside the provider get a helpful throw instead of
 *     a silent null.
 *   - Derived helpers (hasRole, hasLocation) are memoized on the context
 *     object so consumers don't need to re-derive them inline.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMe } from '../api/client';
import type { AppConfig, Location, MeResponse, User } from '../api/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionContextValue {
  /** The raw /me response. */
  me: MeResponse;
  /** Shortcut for me.user */
  user: User;
  /** Shortcut for me.roles */
  roles: string[];
  /** Shortcut for me.locations */
  locations: Location[];
  /** Shortcut for me.config */
  config: AppConfig;
  /** Returns true if the session has any of the supplied roles. */
  hasRole: (...roles: string[]) => boolean;
  /** Returns true if the session's location list includes this ID. */
  hasLocation: (locationId: string) => boolean;
  /** True for Leader and Admin — they can see all locations. */
  isFullAccess: boolean;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SessionContext = createContext<SessionContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the current session. Must be called inside SessionProvider.
 * Throws a descriptive error when called outside the provider boundary
 * (typically a coding mistake caught in development).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession() called outside <SessionProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Query key + fetcher
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export const ME_QUERY_KEY = ['me'] as const;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface SessionProviderProps {
  children: React.ReactNode;
  /** Rendered while /me is loading (spinner, skeleton, etc.). */
  loadingFallback?: React.ReactNode;
  /** Rendered when /me fails. */
  errorFallback?: React.ReactNode;
}

export function SessionProvider({
  children,
  loadingFallback = <DefaultLoadingScreen />,
  errorFallback = <DefaultErrorScreen />,
}: SessionProviderProps): React.ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    // The /me result is stable for the tab's lifetime — no background
    // refetching needed. Role changes require a fresh sign-in.
    // Retry policy is left to the QueryClient default so tests can override
    // it with retry: false.
    staleTime: Infinity,
  });

  const value = useMemo<SessionContextValue | null>(() => {
    if (!data) return null;
    const roleSet = new Set(data.roles);
    const locationSet = new Set(data.locations.map((l) => l.id));
    const isFullAccess = roleSet.has('Leader') || roleSet.has('Admin');
    return {
      me: data,
      user: data.user,
      roles: data.roles,
      locations: data.locations,
      config: data.config,
      hasRole: (...roles: string[]) => roles.some((r) => roleSet.has(r)),
      hasLocation: (locationId: string) =>
        isFullAccess || locationSet.has(locationId),
      isFullAccess,
    };
  }, [data]);

  if (isLoading) return <>{loadingFallback}</>;
  if (isError || !value) return <>{errorFallback}</>;

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// ---------------------------------------------------------------------------
// Default fallback screens
// ---------------------------------------------------------------------------

function DefaultLoadingScreen(): React.ReactElement {
  return (
    <div className="auth-screen" role="status" aria-label="Loading session">
      <div className="auth-screen-inner">
        <div className="spinner" aria-hidden="true" />
        <p className="text-muted">Loading…</p>
      </div>
    </div>
  );
}

function DefaultErrorScreen(): React.ReactElement {
  return (
    <div className="auth-screen" role="alert">
      <div className="auth-screen-inner">
        <h2>Unable to load session</h2>
        <p className="text-muted">
          There was a problem loading your account. Please reload the page or
          sign out and sign back in.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    </div>
  );
}
